import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import {
  PackagePlus, ScanLine, Upload, Sparkles, Package, Truck,
  Plus, Trash2, AlertCircle, Loader2, CheckCircle2, X, History, Camera, Search,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { purchasesAPI, suppliersAPI, productsAPI, catalogAPI, posAPI, incomingInvoicesAPI } from '@/lib/api';
import { markProductsDirty } from '@/lib/offlineSync';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import CameraScanner from '@/components/CameraScanner';
import { useIsMobile } from '@/hooks/useIsMobile';

// ── Helpers ───────────────────────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => `₹${(+n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const GST_OPTIONS = [null, 0, 5, 12, 18, 28];

const GstSelect = ({ value, onChange, className = '' }) => (
  <select
    value={value === null || value === undefined ? '' : value}
    onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
    className={`text-sm border border-border rounded-lg px-2.5 py-2 outline-none focus:border-primary bg-background ${className}`}
  >
    <option value="">GST —</option>
    {[0, 5, 12, 18, 28].map(r => (
      <option key={r} value={r}>{r}%</option>
    ))}
  </select>
);

const EMPTY_ROW = () => ({
  _id:          crypto.randomUUID(),
  product_name: '',
  pack_size:    '',
  barcode:      '',
  qty:          1,
  cost_price:   0,
  gst_rate:     null,
  expiry_date:  '',
  catalog_id:   null,
  is_new:       false,
  resolving:    false,
});

// ── Catalog ID chip ───────────────────────────────────────────────────────────
const CatalogChip = ({ id, isNew }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="font-mono text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate max-w-[120px]">{id.slice(0, 8)}…</span>
    {isNew && (
      <span className="text-[9px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">NEW</span>
    )}
  </span>
);

// ── Staged items table ────────────────────────────────────────────────────────
const StagedTable = ({ items, onUpdate, onRemove }) => {
  if (items.length === 0) return null;

  const total = items.reduce((s, r) => s + (+r.qty || 0) * (+r.cost_price || 0), 0);

  return (
    <div className="space-y-3">
      <div className="border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-xs text-muted-foreground border-b border-border">
              <th className="text-left px-3 py-2.5 font-medium">Product</th>
              <th className="text-left px-3 py-2.5 font-medium w-24">Pack Size</th>
              <th className="text-left px-3 py-2.5 font-medium w-16">Qty</th>
              <th className="text-left px-3 py-2.5 font-medium w-24">Cost Price</th>
              <th className="text-left px-3 py-2.5 font-medium w-20">GST</th>
              <th className="text-left px-3 py-2.5 font-medium w-28">Expiry</th>
              <th className="text-left px-3 py-2.5 font-medium">Catalog ID</th>
              <th className="text-right px-3 py-2.5 font-medium w-24">Total</th>
              <th className="w-8 px-2 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map(row => (
              <tr key={row._id} className="hover:bg-muted/10">
                <td className="px-3 py-2 font-medium">{row.product_name}</td>
                <td className="px-3 py-2 text-muted-foreground text-xs">{row.pack_size || '—'}</td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min="0.01"
                    step="any"
                    value={row.qty}
                    onChange={e => onUpdate(row._id, { qty: e.target.value })}
                    className="w-16 text-sm border border-border rounded px-1.5 py-0.5 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={row.cost_price}
                    onChange={e => onUpdate(row._id, { cost_price: e.target.value })}
                    className="w-20 text-sm border border-border rounded px-1.5 py-0.5 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-3 py-2">
                  <GstSelect value={row.gst_rate} onChange={v => onUpdate(row._id, { gst_rate: v })} className="w-full py-0.5 text-xs" />
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{row.expiry_date || '—'}</td>
                <td className="px-3 py-2">
                  {row.resolving
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    : row.catalog_id
                      ? <CatalogChip id={row.catalog_id} isNew={row.is_new} />
                      : <span className="text-xs text-muted-foreground italic">Pending</span>}
                </td>
                <td className="px-3 py-2 text-right font-medium text-sm">
                  {fmt((+row.qty || 0) * (+row.cost_price || 0))}
                </td>
                <td className="px-2 py-2">
                  <button onClick={() => onRemove(row._id)} className="p-1 text-muted-foreground hover:text-destructive rounded transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <span className="text-base font-semibold">Total: {fmt(total)}</span>
      </div>
    </div>
  );
};

// ── Manual Entry panel ────────────────────────────────────────────────────────
const ManualEntryPanel = ({ onAdd, catalog = [] }) => {
  const [row, setRow] = useState({
    product_name: '', pack_size: '', barcode: '', qty: 1,
    cost_price: '', gst_rate: null, expiry_date: '', _catalog_id: null,
  });
  const [adding, setAdding] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [badge, setBadge] = useState(null); // null | 'loading' | 'existing' | 'new'
  const [suggestingBarcode, setSuggestingBarcode] = useState(false);
  const debounceRef = useRef(null);

  const handleSuggestBarcode = async () => {
    setSuggestingBarcode(true);
    try {
      const res = await purchasesAPI.suggestBarcode();
      set('barcode', res.data.barcode);
    } catch {
      // silently ignore
    } finally {
      setSuggestingBarcode(false);
    }
  };

  const set = (k, v) => setRow(r => ({ ...r, [k]: v }));

  const suggestions = useMemo(() => {
    const q = row.product_name.trim().toLowerCase();
    if (q.length < 2) return [];
    return catalog.filter(p => p.name?.toLowerCase().includes(q)).slice(0, 6);
  }, [catalog, row.product_name]);

  const triggerBadge = useCallback((name, packSize) => {
    clearTimeout(debounceRef.current);
    if (!name || name.trim().length < 2) { setBadge(null); return; }
    setBadge('loading');
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await purchasesAPI.resolveItem({ product_name: name.trim(), pack_size: (packSize || '').trim() });
        setBadge(res.data.is_new ? 'new' : 'existing');
      } catch {
        setBadge(null);
      }
    }, 300);
  }, []);

  const handleProductNameChange = (val) => {
    setRow(r => ({ ...r, product_name: val, _catalog_id: null }));
    setBadge(null);
    setDropdownOpen(true);
    triggerBadge(val, row.pack_size);
  };

  const handlePackSizeChange = (val) => {
    setRow(r => ({ ...r, pack_size: val, _catalog_id: null }));
    setBadge(null);
    triggerBadge(row.product_name, val);
  };

  const handleSelect = (product) => {
    setRow(r => ({
      ...r,
      product_name: product.name,
      pack_size: product.pack_size || '',
      gst_rate: product.gst_rate ?? r.gst_rate,
      _catalog_id: product.catalog_id,
    }));
    setBadge('existing');
    setDropdownOpen(false);
    clearTimeout(debounceRef.current);
  };

  const handleAdd = async () => {
    if (!row.product_name.trim()) { toast.error('Product name is required'); return; }
    if (!row.cost_price || +row.cost_price <= 0) { toast.error('Cost price must be > 0'); return; }
    setAdding(true);
    try {
      let catalog_id = row._catalog_id;
      let is_new = false;
      if (!catalog_id) {
        const res = await purchasesAPI.resolveItem({ product_name: row.product_name.trim(), pack_size: row.pack_size.trim() });
        catalog_id = res.data.catalog_id;
        is_new = res.data.is_new;
      }
      if (is_new) toast.success(`New product added to stock master: ${row.product_name}`);
      onAdd({
        ...EMPTY_ROW(),
        product_name: row.product_name.trim(),
        pack_size:    row.pack_size.trim(),
        barcode:      row.barcode.trim(),
        qty:          +row.qty || 1,
        cost_price:   +row.cost_price,
        gst_rate:     row.gst_rate,
        expiry_date:  row.expiry_date,
        catalog_id,
        is_new,
      });
      setRow({ product_name: '', pack_size: '', barcode: '', qty: 1, cost_price: '', gst_rate: null, expiry_date: '', _catalog_id: null });
      setBadge(null);
      setDropdownOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resolve product');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="border border-border rounded-xl p-4 bg-card">
      <div className="flex items-end gap-2 flex-wrap">
        {/* Product Name */}
        <div className="flex-1 min-w-[140px] relative">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-muted-foreground">Product Name *</label>
            {badge === 'loading'  && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            {badge === 'existing' && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">✓ Existing</span>}
            {badge === 'new'      && <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">+ New item</span>}
          </div>
          <input
            value={row.product_name}
            onChange={e => handleProductNameChange(e.target.value)}
            onFocus={() => row.product_name.trim().length >= 2 && setDropdownOpen(true)}
            onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="e.g. Tata Salt"
            className="w-full text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary"
          />
          {dropdownOpen && suggestions.length > 0 && (
            <ul className="absolute z-20 top-full mt-1 w-full bg-card border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
              {suggestions.map(p => (
                <li
                  key={p.catalog_id}
                  onMouseDown={() => handleSelect(p)}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 first:rounded-t-xl last:rounded-b-xl flex items-center justify-between gap-2"
                >
                  <span className="font-medium truncate">{p.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {[p.pack_size, p.category, p.brand].filter(Boolean).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pack Size */}
        <div className="w-20">
          <label className="text-xs text-muted-foreground mb-1 block">Pack Size</label>
          <input
            value={row.pack_size}
            onChange={e => handlePackSizeChange(e.target.value)}
            placeholder="1kg"
            className="w-full text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary"
          />
        </div>

        {/* Barcode */}
        <div className="w-28">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-muted-foreground">Barcode</label>
            <button
              type="button"
              onClick={handleSuggestBarcode}
              disabled={suggestingBarcode}
              className="text-[10px] text-primary underline underline-offset-2 hover:opacity-70 disabled:opacity-40 transition-opacity"
            >
              {suggestingBarcode ? 'Suggesting…' : 'Suggest code'}
            </button>
          </div>
          <input
            value={row.barcode}
            onChange={e => set('barcode', e.target.value)}
            placeholder="Scan or type"
            className="w-full text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary"
          />
        </div>

        {/* Qty */}
        <div className="w-16">
          <label className="text-xs text-muted-foreground mb-1 block">Qty</label>
          <input
            type="number" min="0.01" step="any"
            value={row.qty}
            onChange={e => set('qty', e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary"
          />
        </div>

        {/* Cost Price */}
        <div className="w-24">
          <label className="text-xs text-muted-foreground mb-1 block">Cost Price (₹) *</label>
          <input
            type="number" min="0" step="any"
            value={row.cost_price}
            onChange={e => set('cost_price', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="0"
            className="w-full text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary"
          />
        </div>

        {/* GST Rate */}
        <div className="w-20">
          <label className="text-xs text-muted-foreground mb-1 block">GST %</label>
          <GstSelect value={row.gst_rate} onChange={v => set('gst_rate', v)} className="w-full" />
        </div>

        {/* Expiry */}
        <div className="w-36">
          <label className="text-xs text-muted-foreground mb-1 block">Expiry Date</label>
          <input
            type="date"
            value={row.expiry_date}
            onChange={e => set('expiry_date', e.target.value)}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary"
          />
        </div>

        <Button onClick={handleAdd} disabled={adding} size="sm" className="flex items-center gap-1.5 shrink-0">
          {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Add Item
        </Button>
      </div>
    </div>
  );
};

// ── Barcode Found Modal (desktop) ─────────────────────────────────────────────
const BarcodeFoundModal = ({ product, onConfirm, onCancel }) => {
  const [qty, setQty]               = useState('1');
  const [costPrice, setCost]        = useState('');
  const [sellingPrice, setSelling]  = useState(product.mrp ? String(product.mrp) : '');
  const [gstRate, setGst]           = useState(product.gst_rate ?? null);
  const [expiry, setExpiry]         = useState('');
  const qtyRef = useRef(null);

  useEffect(() => { qtyRef.current?.select(); }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);

  const handleConfirm = () => {
    if (!qty || +qty <= 0) { toast.error('Quantity must be > 0'); return; }
    if (!costPrice || +costPrice <= 0) { toast.error('Cost price must be > 0'); return; }
    if (!sellingPrice || +sellingPrice <= 0) { toast.error('MRP must be > 0'); return; }
    onConfirm({
      qty:           +qty,
      cost_price:    +costPrice,
      mrp: +sellingPrice,
      original_mrp: product.mrp ?? null,
      gst_rate:      gstRate,
      expiry_date:   expiry,
    });
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleConfirm(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-gradient-to-br from-emerald-500 to-green-600 px-6 pt-6 pb-5 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-100 mb-0.5">Found in stock master</p>
                <h2 className="text-xl font-bold leading-tight">{product.product_name}</h2>
                {product.pack_size && <p className="text-sm text-emerald-100 mt-0.5">{product.pack_size}</p>}
              </div>
            </div>
            <button onClick={onCancel} className="mt-0.5 text-white/70 hover:text-white transition-colors shrink-0 ml-2">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Quantity <span className="text-red-500">*</span>
            </label>
            <input ref={qtyRef} type="number" min="0.01" step="any" value={qty}
              onChange={e => setQty(e.target.value)} onKeyDown={handleKeyDown} placeholder="0"
              className="w-full text-lg font-semibold border-2 border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 transition-colors" />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Cost Price (₹) <span className="text-red-500">*</span>
            </label>
            <input type="number" min="0" step="any" value={costPrice}
              onChange={e => setCost(e.target.value)} onKeyDown={handleKeyDown} placeholder="0.00"
              className="w-full text-lg font-semibold border-2 border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 transition-colors" />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              MRP (₹) <span className="text-red-500">*</span>
              <span className="ml-1 text-[10px] normal-case font-normal text-slate-400">(pre-filled from master)</span>
            </label>
            <input type="number" min="0" step="any" value={sellingPrice}
              onChange={e => setSelling(e.target.value)} onKeyDown={handleKeyDown} placeholder="0.00"
              className="w-full text-lg font-semibold border-2 border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-emerald-500 transition-colors" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">GST Rate</label>
              <GstSelect value={gstRate} onChange={setGst} className="w-full border-2 border-slate-200 focus:border-emerald-500 py-2.5 rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Expiry Date</label>
              <input type="date" value={expiry} onChange={e => setExpiry(e.target.value)}
                className="w-full text-sm border-2 border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-emerald-500 transition-colors" />
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 space-y-2">
          <button onClick={handleConfirm}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold text-base shadow-md hover:from-emerald-600 hover:to-green-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2">
            <Plus className="h-4 w-4" /> Add to Purchase
          </button>
          <button onClick={onCancel} className="w-full text-center text-sm text-slate-400 hover:text-slate-600 py-1 transition-colors">
            Cancel (Esc)
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Mobile unified barcode modal ──────────────────────────────────────────────
// state: { phase:'loading', barcode } | { phase:'found', product_name, pack_size, catalog_id, gst_rate, barcode, mrp }
//       | { phase:'new', barcode }
const BarcodeProductModal = ({ state, onConfirm, onCancel, saving }) => {
  const [productName,   setProductName]  = useState('');
  const [packSize,      setPackSize]     = useState('');
  const [qty,           setQty]          = useState('1');
  const [costPrice,     setCostPrice]    = useState('');
  const [sellingPrice,  setSellingPrice] = useState('');
  const [gstRate,       setGstRate]      = useState(null);
  const [expiry,        setExpiry]       = useState('');
  const qtyRef  = useRef(null);
  const nameRef = useRef(null);

  const isLoading = state?.phase === 'loading';
  const isFound   = state?.phase === 'found';
  const isNew     = state?.phase === 'new';

  // Populate selling price + gst when found product arrives
  useEffect(() => {
    if (isFound) {
      setGstRate(state.gst_rate ?? null);
      setSellingPrice(state.mrp ? String(state.mrp) : '');
    }
  }, [isFound, state?.catalog_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus after phase resolves
  useEffect(() => {
    if (isFound)  setTimeout(() => qtyRef.current?.select(), 60);
    if (isNew)    setTimeout(() => nameRef.current?.focus(), 60);
  }, [isFound, isNew]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && !saving) onCancel(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onCancel, saving]);

  const handleConfirm = () => {
    if (isLoading) return;
    if (isFound) {
      if (!qty || +qty <= 0) { toast.error('Quantity must be > 0'); return; }
      if (!costPrice || +costPrice <= 0) { toast.error('Cost price must be > 0'); return; }
      if (!sellingPrice || +sellingPrice <= 0) { toast.error('MRP must be > 0'); return; }
      onConfirm({ qty: +qty, cost_price: +costPrice, mrp: +sellingPrice, gst_rate: gstRate, expiry_date: expiry });
    } else if (isNew) {
      if (!productName.trim()) { toast.error('Product name is required'); return; }
      if (!costPrice || +costPrice <= 0) { toast.error('Cost price must be > 0'); return; }
      if (!sellingPrice || +sellingPrice <= 0) { toast.error('MRP must be > 0'); return; }
      onConfirm({
        product_name:  productName.trim(),
        pack_size:     packSize.trim(),
        qty:           +qty || 1,
        cost_price:    +costPrice,
        mrp: +sellingPrice,
        gst_rate:      gstRate,
        expiry_date:   expiry,
      });
    }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !saving) handleConfirm(); };

  const headerCls = isFound
    ? 'from-emerald-500 to-green-600'
    : isNew
    ? 'from-amber-500 to-yellow-500'
    : 'from-slate-400 to-slate-500';

  const focusCls = isFound ? 'focus:border-emerald-500' : isNew ? 'focus:border-amber-500' : '';
  const btnCls   = isFound
    ? 'from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700'
    : 'from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-card rounded-t-2xl shadow-2xl w-full overflow-hidden animate-in fade-in slide-in-from-bottom duration-200 max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className={`bg-gradient-to-br ${headerCls} px-5 pt-5 pb-4 text-white flex-none`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                {isLoading
                  ? <Loader2 className="h-5 w-5 text-white animate-spin" />
                  : isFound
                  ? <CheckCircle2 className="h-5 w-5 text-white" />
                  : <AlertCircle className="h-5 w-5 text-white" />}
              </div>
              <div>
                {isLoading && (
                  <>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-white/70 mb-0.5">Looking up…</p>
                    <p className="text-sm font-mono text-white/80">{state.barcode}</p>
                  </>
                )}
                {isFound && (
                  <>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-100 mb-0.5">Found in stock master</p>
                    <h2 className="text-lg font-bold leading-tight">{state.product_name}</h2>
                    {state.pack_size && <p className="text-xs text-emerald-100 mt-0.5">{state.pack_size}</p>}
                  </>
                )}
                {isNew && (
                  <>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-100 mb-0.5">New product</p>
                    <p className="text-sm font-mono text-white/80">{state.barcode}</p>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded mt-1 inline-block">+ New item</span>
                  </>
                )}
              </div>
            </div>
            <button onClick={onCancel} disabled={saving}
              className="text-white/70 hover:text-white transition-colors ml-2 shrink-0 disabled:opacity-40">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center py-8">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-4 space-y-3.5">
              {/* New product: name + pack size */}
              {isNew && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                      Product Name <span className="text-red-500">*</span>
                    </label>
                    <input ref={nameRef} value={productName} onChange={e => setProductName(e.target.value)}
                      onKeyDown={onKey} placeholder="e.g. Tata Salt"
                      className={`w-full text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 outline-none ${focusCls} transition-colors`} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Pack Size</label>
                    <input value={packSize} onChange={e => setPackSize(e.target.value)} onKeyDown={onKey} placeholder="e.g. 1kg"
                      className={`w-full text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 outline-none ${focusCls} transition-colors`} />
                  </div>
                </>
              )}

              {/* Qty */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                  Quantity <span className="text-red-500">*</span>
                </label>
                <input ref={qtyRef} type="number" min="0.01" step="any" value={qty}
                  onChange={e => setQty(e.target.value)} onKeyDown={onKey} placeholder="0"
                  className={`w-full text-lg font-semibold border-2 border-slate-200 rounded-xl px-4 py-2.5 outline-none ${focusCls} transition-colors`} />
              </div>

              {/* Cost Price */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                  Cost Price (₹) <span className="text-red-500">*</span>
                </label>
                <input type="number" min="0" step="any" value={costPrice}
                  onChange={e => setCostPrice(e.target.value)} onKeyDown={onKey} placeholder="0.00"
                  className={`w-full text-lg font-semibold border-2 border-slate-200 rounded-xl px-4 py-2.5 outline-none ${focusCls} transition-colors`} />
              </div>

              {/* MRP */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
                  MRP (₹) <span className="text-red-500">*</span>
                  {isFound && <span className="ml-1 text-[10px] normal-case font-normal text-slate-400">(pre-filled from master)</span>}
                </label>
                <input type="number" min="0" step="any" value={sellingPrice}
                  onChange={e => setSellingPrice(e.target.value)} onKeyDown={onKey} placeholder="0.00"
                  className={`w-full text-lg font-semibold border-2 border-slate-200 rounded-xl px-4 py-2.5 outline-none ${focusCls} transition-colors`} />
              </div>

              {/* GST + Expiry */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">GST Rate</label>
                  <GstSelect value={gstRate} onChange={setGstRate}
                    className={`w-full border-2 border-slate-200 py-2.5 rounded-xl ${focusCls}`} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Expiry</label>
                  <input type="date" value={expiry} onChange={e => setExpiry(e.target.value)}
                    className={`w-full text-sm border-2 border-slate-200 rounded-xl px-3 py-2.5 outline-none ${focusCls} transition-colors`} />
                </div>
              </div>
            </div>

            <div className="px-5 pb-6 pt-1 space-y-2">
              <button onClick={handleConfirm} disabled={saving}
                className={`w-full py-3.5 rounded-xl bg-gradient-to-r ${btnCls} text-white font-bold text-base shadow-md active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60`}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {saving ? 'Saving…' : 'Add Product'}
              </button>
              <button onClick={onCancel} disabled={saving}
                className="w-full text-center text-sm text-slate-400 hover:text-slate-600 py-1 transition-colors disabled:opacity-40">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Barcode Scan panel ────────────────────────────────────────────────────────
const BarcodeScanPanel = ({ onAdd, onSaveNow, products = [] }) => {
  const searchInputRef  = useRef(null);
  const dropdownRef     = useRef(null);
  const bufferRef       = useRef('');
  const lastKeyRef      = useRef(0);
  const scanCooldownRef = useRef(0);
  const cameraSourceRef = useRef(false);
  const isMobile        = useIsMobile();

  const [searchQuery,        setSearchQuery]        = useState('');
  const [searchResults,      setSearchResults]      = useState([]);
  const [showDropdown,       setShowDropdown]       = useState(false);
  const [productHighlightIdx, setProductHighlightIdx] = useState(-1);

  // Mobile state — unified modal
  const [mobileModal,  setMobileModal]  = useState(null);
  const [modalSaving,  setModalSaving]  = useState(false);

  // Desktop state — separate found modal + Quick Add popup
  const [foundProduct,       setFoundProduct]       = useState(null);
  const [looking,            setLooking]            = useState(false);
  const [qaProductOpen,      setQaProductOpen]      = useState(false);
  const [qaProductBarcode,   setQaProductBarcode]   = useState('');
  const [qaProductForm,      setQaProductForm]      = useState({ name: '', pack_size: '', qty: '1', cost_price: '', mrp: '', gst_rate: null, expiry_date: '' });
  const [qaSuggestingBarcode, setQaSuggestingBarcode] = useState(false);

  const [cameraOpen, setCameraOpen] = useState(false);
  useEffect(() => { if (isMobile) setCameraOpen(true); }, [isMobile]);

  const isAnyModalOpen = isMobile
    ? mobileModal !== null
    : foundProduct !== null || qaProductOpen;

  const focusInput = useCallback(() => {
    setTimeout(() => { searchInputRef.current?.focus(); }, 50);
  }, []);

  // Auto-scroll highlighted dropdown item into view
  useEffect(() => {
    if (!dropdownRef.current || productHighlightIdx < 0) return;
    const buttons = dropdownRef.current.querySelectorAll('button');
    if (buttons[productHighlightIdx]) {
      buttons[productHighlightIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [productHighlightIdx]);

  const doSearch = useCallback((q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); setShowDropdown(false); setProductHighlightIdx(-1); return; }
    const lower = q.toLowerCase();
    const score = p => {
      const bc = String(p.barcode || '').toLowerCase();
      const nm = (p.name || p.product_name || '').toLowerCase();
      if (bc === lower) return 0;
      if (bc.startsWith(lower)) return 1;
      if (nm.startsWith(lower)) return 2;
      if (bc.includes(lower)) return 3;
      return 4;
    };
    const results = products
      .filter(p => {
        const nm = (p.name || p.product_name || '').toLowerCase();
        return nm.includes(lower) || String(p.barcode || '').toLowerCase().includes(lower);
      })
      .sort((a, b) => score(a) - score(b))
      .slice(0, 20);
    setSearchResults(results);
    setShowDropdown(results.length > 0);
    setProductHighlightIdx(results.length > 0 ? 0 : -1);
  }, [products]);

  const doLookup = useCallback(async (barcode) => {
    if (!barcode.trim()) return;
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    if (isMobile) {
      setMobileModal({ phase: 'loading', barcode: barcode.trim() });
    } else {
      setLooking(true);
      setFoundProduct(null);
      setQaProductOpen(false);
    }
    try {
      const res = await purchasesAPI.lookupBarcode(barcode.trim());
      const { name, pack_size, catalog_id, gst_rate, mrp } = res.data;
      if (isMobile) {
        setMobileModal({ phase: 'found', product_name: name || '', pack_size: pack_size || '', catalog_id, gst_rate: gst_rate ?? null, barcode: barcode.trim(), mrp: mrp ?? null });
      } else {
        setFoundProduct({ product_name: name || '', pack_size: pack_size || '', catalog_id, gst_rate: gst_rate ?? null, barcode: barcode.trim(), mrp: mrp ?? null });
      }
    } catch {
      if (isMobile) {
        setMobileModal({ phase: 'new', barcode: barcode.trim() });
      } else {
        openQaProductModal(barcode.trim());
      }
    } finally {
      if (!isMobile) setLooking(false);
    }
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Select a product directly from search results (no API call needed)
  const doSelect = useCallback((product) => {
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    const data = {
      product_name:  product.name || product.product_name || '',
      pack_size:     product.pack_size || '',
      catalog_id:    product.catalog_id,
      gst_rate:      product.gst_rate ?? null,
      barcode:       product.barcode || '',
      mrp: product.mrp ?? null,
    };
    if (isMobile) {
      setMobileModal({ phase: 'found', ...data });
    } else {
      setFoundProduct(data);
    }
  }, [isMobile]);

  // Global keyboard listener — fires only when no input element is focused
  useEffect(() => {
    const handleGlobal = (e) => {
      if (isAnyModalOpen) return;
      if (Date.now() - scanCooldownRef.current < 600) return;
      const active = document.activeElement;
      const tag = active?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || active?.isContentEditable) return;
      if (e.key.length !== 1 && e.key !== 'Enter') return;
      const now = Date.now();
      if (now - lastKeyRef.current < 80) {
        bufferRef.current += e.key === 'Enter' ? '' : e.key;
      } else {
        bufferRef.current = e.key === 'Enter' ? '' : e.key;
      }
      lastKeyRef.current = now;
      if (e.key !== 'Enter' || !bufferRef.current.trim()) return;
      const barcode = bufferRef.current.trim();
      bufferRef.current = '';
      e.preventDefault();
      doLookup(barcode);
    };
    document.addEventListener('keydown', handleGlobal);
    return () => document.removeEventListener('keydown', handleGlobal);
  }, [doLookup, isAnyModalOpen]);

  // ── Desktop confirm handlers ──────────────────────────────────────────────
  const handleFoundConfirm = ({ qty, cost_price, mrp, original_mrp, gst_rate, expiry_date }) => {
    if (!foundProduct) return;
    onAdd({
      ...EMPTY_ROW(),
      product_name: foundProduct.product_name,
      pack_size:    foundProduct.pack_size,
      barcode:      foundProduct.barcode,
      qty, cost_price, mrp, original_mrp,
      gst_rate, expiry_date,
      catalog_id:   foundProduct.catalog_id,
      is_new:       false,
    });
    setFoundProduct(null);
    scanCooldownRef.current = Date.now();
    focusInput();
  };

  const openQaProductModal = useCallback((input) => {
    const trimmed = (input || '').trim();
    const onlyLetters = trimmed.length > 0 && /^[a-zA-Z\s]+$/.test(trimmed);
    if (onlyLetters) {
      setQaProductBarcode('');
      setQaProductForm({ name: trimmed, pack_size: '', qty: '1', cost_price: '', mrp: '', gst_rate: null, expiry_date: '' });
    } else if (trimmed.length > 0) {
      setQaProductBarcode(trimmed);
      setQaProductForm({ name: '', pack_size: '', qty: '1', cost_price: '', mrp: '', gst_rate: null, expiry_date: '' });
    } else {
      setQaProductBarcode('');
      setQaProductForm({ name: '', pack_size: '', qty: '1', cost_price: '', mrp: '', gst_rate: null, expiry_date: '' });
    }
    setSearchQuery('');
    setShowDropdown(false);
    setQaProductOpen(true);
  }, []);

  const handleQuickAddProduct = () => {
    const { name, pack_size, qty, cost_price, mrp, gst_rate, expiry_date } = qaProductForm;
    if (!name.trim()) { toast.error('Product name is required'); return; }
    if (!cost_price || +cost_price <= 0) { toast.error('Cost price must be > 0'); return; }
    if (!mrp || +mrp <= 0) { toast.error('MRP must be > 0'); return; }
    onAdd({
      ...EMPTY_ROW(),
      product_name:  name.trim(),
      pack_size:     (pack_size || '').trim(),
      barcode:       qaProductBarcode,
      qty:           +qty || 1,
      cost_price:    +cost_price,
      mrp: +mrp,
      gst_rate,
      expiry_date,
      catalog_id:    null,
      is_new:        true,
    });
    setQaProductOpen(false);
    setQaProductBarcode('');
    setQaProductForm({ name: '', pack_size: '', qty: '1', cost_price: '', mrp: '', gst_rate: null, expiry_date: '' });
    scanCooldownRef.current = Date.now();
    toast.success('New product will be added to stock master on save');
    focusInput();
  };

  // ── Mobile confirm handler ────────────────────────────────────────────────
  const handleMobileConfirm = useCallback(async (formData) => {
    if (!mobileModal || mobileModal.phase === 'loading') return;
    let item;
    if (mobileModal.phase === 'found') {
      item = {
        ...EMPTY_ROW(),
        product_name: mobileModal.product_name,
        pack_size:    mobileModal.pack_size,
        barcode:      mobileModal.barcode,
        qty:          formData.qty,
        cost_price:   formData.cost_price,
        mrp: formData.mrp,
        original_mrp: mobileModal.mrp,
        gst_rate:     formData.gst_rate,
        expiry_date:  formData.expiry_date,
        catalog_id:   mobileModal.catalog_id,
        is_new:       false,
      };
    } else {
      item = {
        ...EMPTY_ROW(),
        product_name:  formData.product_name,
        pack_size:     formData.pack_size,
        barcode:       mobileModal.barcode,
        qty:           formData.qty,
        cost_price:    formData.cost_price,
        mrp: formData.mrp,
        gst_rate:      formData.gst_rate,
        expiry_date:   formData.expiry_date,
        catalog_id:    null,
        is_new:        true,
      };
    }
    setModalSaving(true);
    const ok = await onSaveNow(item);
    setModalSaving(false);
    if (ok) {
      setMobileModal(null);
      scanCooldownRef.current = Date.now();
      if (cameraSourceRef.current) {
        cameraSourceRef.current = false;
        setCameraOpen(true);
      } else {
        focusInput();
      }
    }
    // on failure: modal stays open (error toasted by onSaveNow)
  }, [mobileModal, onSaveNow, focusInput]);

  const handleMobileCancel = useCallback(() => {
    cameraSourceRef.current = false;
    setMobileModal(null);
    setModalSaving(false);
    scanCooldownRef.current = Date.now();
    focusInput();
  }, [focusInput]);

  return (
    <>
      {cameraOpen && (
        <CameraScanner
          inline
          onDetected={(barcode) => {
            setCameraOpen(false);
            if (!isAnyModalOpen && Date.now() - scanCooldownRef.current >= 600) {
              cameraSourceRef.current = true;
              doLookup(barcode);
            }
          }}
          onClose={() => setCameraOpen(false)}
        />
      )}

      {/* Mobile: unified modal (loading → found/new) */}
      {isMobile && mobileModal && (
        <BarcodeProductModal
          state={mobileModal}
          onConfirm={handleMobileConfirm}
          onCancel={handleMobileCancel}
          saving={modalSaving}
        />
      )}

      {/* Desktop: separate found modal */}
      {!isMobile && foundProduct && (
        <BarcodeFoundModal
          product={foundProduct}
          onConfirm={handleFoundConfirm}
          onCancel={() => {
            setFoundProduct(null);
            scanCooldownRef.current = Date.now();
            focusInput();
          }}
        />
      )}

      <div className="border border-border rounded-xl p-4 bg-card space-y-3">
        {/* Unified search bar — same on mobile and desktop */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            ref={searchInputRef}
            autoFocus={!isMobile}
            type="text"
            value={searchQuery}
            onChange={e => doSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!showDropdown && searchResults.length > 0) { setShowDropdown(true); setProductHighlightIdx(0); }
                else setProductHighlightIdx(i => Math.min(i + 1, searchResults.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setProductHighlightIdx(i => Math.max(i - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                if (showDropdown && productHighlightIdx >= 0 && productHighlightIdx < searchResults.length) {
                  doSelect(searchResults[productHighlightIdx]);
                } else if (searchQuery.trim()) {
                  doLookup(searchQuery.trim());
                }
              } else if (e.key === 'Escape') {
                setSearchQuery(''); setSearchResults([]); setShowDropdown(false); setProductHighlightIdx(-1);
              }
            }}
            onFocus={() => { if (searchQuery.trim() && searchResults.length > 0) setShowDropdown(true); }}
            onBlur={() => setTimeout(() => { setShowDropdown(false); setProductHighlightIdx(-1); }, 150)}
            placeholder="Search by name or scan barcode…"
            className="w-full text-sm border border-border rounded-lg pl-9 pr-14 py-2.5 outline-none focus:border-primary bg-background"
          />
          <button
            type="button"
            onClick={() => setCameraOpen(c => !c)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-2.5 rounded hover:bg-muted transition-colors"
            title="Scan with camera"
          >
            {(looking || mobileModal?.phase === 'loading') ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <Camera className={`h-4 w-4 ${cameraOpen ? 'text-primary' : 'text-muted-foreground'}`} />
            )}
          </button>

          {showDropdown && searchResults.length > 0 && (
            <div ref={dropdownRef} className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
              {searchResults.map((p, i) => {
                const nm   = p.name || p.product_name || '';
                const pack = p.pack_size ? ` (${p.pack_size})` : '';
                return (
                  <button
                    key={p.catalog_id || i}
                    type="button"
                    onMouseDown={() => doSelect(p)}
                    onMouseEnter={() => setProductHighlightIdx(i)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-start justify-between gap-2 border-b border-border last:border-0 ${
                      i === productHighlightIdx ? 'bg-primary/10' : 'hover:bg-muted'
                    }`}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium">{nm}{pack}</span>
                      {p.barcode && <span className="text-xs text-muted-foreground font-mono">{p.barcode}</span>}
                    </div>
                    {p.stock != null && (
                      <span className={`text-xs shrink-0 font-medium mt-0.5 ${p.stock <= 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                        Qty: {p.stock}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* Desktop: Quick Add Product popup modal */}
      {!isMobile && qaProductOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 pt-5 pb-4 flex items-start justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-100 mb-0.5">New product</p>
                <h2 className="text-lg font-bold text-white leading-tight flex items-center gap-2">
                  <Package className="h-4 w-4" /> Quick Add Product
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wide text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded">+ New item</span>
                <button onClick={() => { setQaProductOpen(false); setQaProductBarcode(''); setQaProductForm({ name: '', pack_size: '', qty: '1', cost_price: '', mrp: '', gst_rate: null, expiry_date: '' }); scanCooldownRef.current = Date.now(); }}
                  className="text-white/70 hover:text-white transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Product Name <span className="text-red-500">*</span></label>
                <input type="text" value={qaProductForm.name} autoFocus
                  onChange={e => setQaProductForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Tata Salt"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Barcode <span className="text-muted-foreground text-xs font-normal">(optional)</span></label>
                <input type="text" value={qaProductBarcode}
                  onChange={e => setQaProductBarcode(e.target.value)}
                  placeholder="Scan or enter barcode"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                <button type="button" disabled={qaSuggestingBarcode}
                  onClick={async () => {
                    setQaSuggestingBarcode(true);
                    try { const res = await purchasesAPI.suggestBarcode(); setQaProductBarcode(res.data.barcode); }
                    catch { /* ignore */ } finally { setQaSuggestingBarcode(false); }
                  }}
                  className="mt-0.5 text-[10px] text-primary underline underline-offset-2 hover:opacity-70 disabled:opacity-40 transition-opacity">
                  {qaSuggestingBarcode ? 'Suggesting…' : 'Suggest code'}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pack Size <span className="text-muted-foreground text-xs font-normal">(optional)</span></label>
                <input type="text" value={qaProductForm.pack_size}
                  onChange={e => setQaProductForm(f => ({ ...f, pack_size: e.target.value }))}
                  placeholder="e.g. 1kg"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Qty <span className="text-red-500">*</span></label>
                  <input type="number" min="0.01" step="any" value={qaProductForm.qty}
                    onChange={e => setQaProductForm(f => ({ ...f, qty: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cost Price (₹) <span className="text-red-500">*</span></label>
                  <input type="number" min="0" step="any" value={qaProductForm.cost_price}
                    onChange={e => setQaProductForm(f => ({ ...f, cost_price: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">MRP (₹) <span className="text-red-500">*</span></label>
                <input type="number" min="0" step="any" value={qaProductForm.mrp}
                  onChange={e => setQaProductForm(f => ({ ...f, mrp: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleQuickAddProduct(); }}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">GST %</label>
                  <GstSelect value={qaProductForm.gst_rate} onChange={v => setQaProductForm(f => ({ ...f, gst_rate: v }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expiry Date</label>
                  <input type="date" value={qaProductForm.expiry_date}
                    onChange={e => setQaProductForm(f => ({ ...f, expiry_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => { setQaProductOpen(false); setQaProductBarcode(''); setQaProductForm({ name: '', pack_size: '', qty: '1', cost_price: '', mrp: '', gst_rate: null, expiry_date: '' }); scanCooldownRef.current = Date.now(); }}
                  className="flex-1 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60">
                  Cancel
                </button>
                <button onClick={handleQuickAddProduct}
                  disabled={!qaProductForm.name.trim() || !qaProductForm.cost_price || !qaProductForm.mrp}
                  className="flex-1 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                  <Package className="h-4 w-4" />
                  Add to Purchase
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Catalog fuzzy matching ────────────────────────────────────────────────────
// Jaccard similarity on word tokens — fast, no dependencies, good for product names
function _catalogSimilarity(a, b) {
  const tok = s => new Set(
    s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean)
  );
  const ta = tok(a), tb = tok(b);
  let intersect = 0;
  for (const t of ta) if (tb.has(t)) intersect++;
  const union = new Set([...ta, ...tb]).size;
  return union === 0 ? 0 : intersect / union;
}

function _findCatalogMatch(name, packSize, catalog) {
  if (!name || !catalog.length) return null;
  // Include pack_size in the query so "Tata Salt 1kg" beats "Tata Salt 500g"
  const query = [name, packSize].filter(Boolean).join(' ');
  let best = null, bestScore = 0;
  for (const product of catalog) {
    const target = [product.name, product.pack_size].filter(Boolean).join(' ');
    const score = _catalogSimilarity(query, target);
    if (score > bestScore) { bestScore = score; best = product; }
  }
  return bestScore >= 0.55 ? best : null;
}

// ── Upload Invoice panel ──────────────────────────────────────────────────────
const confColor = (c) => {
  if (c === 'high')   return 'text-emerald-700 bg-emerald-100';
  if (c === 'medium') return 'text-amber-700 bg-amber-100';
  return 'text-red-700 bg-red-100';
};

const confBorder = (c) => {
  if (c === 'high')   return 'border-emerald-300 focus:border-emerald-500';
  if (c === 'medium') return 'border-amber-300 focus:border-amber-500';
  return 'border-red-300 focus:border-red-500';
};

const UploadInvoicePanel = ({ onStageAll, onParsedMeta, onSaveDirectly, catalog = [] }) => {
  const [stage,       setStage]       = useState('idle'); // idle | uploading | reviewing | saving
  const [parsedData,  setParsedData]  = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [dragOver,    setDragOver]    = useState(false);
  const fileRef = useRef(null);

  const parseFile = async (file) => {
    if (!file) return;
    const ok = /\.(pdf|jpg|jpeg|png|webp)$/i.test(file.name) ||
      ['application/pdf','image/jpeg','image/png','image/webp'].includes(file.type);
    if (!ok) { toast.error('Upload a PDF or image (JPG, PNG, WEBP)'); return; }
    setStage('uploading');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res  = await purchasesAPI.parseInvoice(fd);
      const data = res.data;
      if (data.parsing_mode === 'failed' || !data.items?.length) {
        const detail = data.error_detail ? `: ${data.error_detail}` : '';
        toast.error(`Could not read invoice — try a clearer photo or a different file${detail}`);
        setStage('idle');
        return;
      }
      onParsedMeta({
        supplier_name: data.supplier_name || '',
        date:          data.date          || '',
        bill_number:   data.bill_number   || '',
      });
      setReviewItems((data.items || []).map(item => {
        const match = _findCatalogMatch(item.product_name, item.pack_size, catalog);
        return {
          _id:           crypto.randomUUID(),
          product_name:  item.product_name || '',
          pack_size:     item.pack_size    || '',
          qty:           item.quantity     ?? 1,
          cost_price:    item.unit_price   ?? 0,
          mrp: item.mrp          ?? null,
          gst_rate:      item.gst_rate     ?? null,
          expiry_date:   item.expiry_date  || '',
          catalog_id:    match?.catalog_id  ?? null,
          is_new:        !match,
          resolving:     false,
        };
      }));
      setParsedData(data);
      setStage('reviewing');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse invoice — please try again');
      setStage('idle');
    }
  };

  const updateRow = (id, patch) => setReviewItems(prev => prev.map(r => r._id === id ? { ...r, ...patch } : r));
  const removeRow = (id)        => setReviewItems(prev => prev.filter(r => r._id !== id));

  const handleConfirm = async () => {
    const valid   = reviewItems.filter(r => r.product_name.trim() && +r.cost_price > 0);
    const skipped = reviewItems.length - valid.length;
    if (!valid.length) { toast.error('No valid items — fill in product name and cost price for at least one row'); return; }
    const mapped = valid.map(r => ({ ...EMPTY_ROW(), ...r, qty: +r.qty || 1, cost_price: +r.cost_price }));
    if (onSaveDirectly) {
      // Mobile: direct save to stock
      setStage('saving');
      const ok = await onSaveDirectly(mapped);
      if (ok) {
        if (skipped) toast.info(`${skipped} item(s) skipped — missing name or price`);
        toast.success(`${valid.length} item${valid.length !== 1 ? 's' : ''} added to stock`);
        setStage('idle'); setParsedData(null); setReviewItems([]);
        if (fileRef.current) fileRef.current.value = '';
      } else {
        setStage('reviewing'); // keep review open so user can retry
      }
    } else {
      // Desktop: stage items for manual review + submit
      onStageAll(mapped);
      if (skipped) toast.info(`${skipped} item(s) skipped — missing name or price`);
      toast.success(`${valid.length} item${valid.length !== 1 ? 's' : ''} added from invoice`);
      setStage('idle'); setParsedData(null); setReviewItems([]);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const reset = () => {
    setStage('idle'); setParsedData(null); setReviewItems([]);
    if (fileRef.current) fileRef.current.value = '';
  };

  if (stage === 'idle') return (
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); parseFile(e.dataTransfer.files[0]); }}
      onClick={() => fileRef.current?.click()}
      className={`border-2 border-dashed rounded-xl p-8 bg-card flex flex-col items-center justify-center text-center space-y-4 min-h-[180px] cursor-pointer transition-colors ${
        dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/20'
      }`}
    >
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
        onChange={e => parseFile(e.target.files[0])} />
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
        <Upload className="h-6 w-6 text-primary" />
      </div>
      <div>
        <p className="font-semibold text-foreground">Upload Supplier Invoice</p>
        <p className="text-sm text-muted-foreground mt-1">Drop a file or click to browse</p>
        <p className="text-xs text-muted-foreground mt-0.5">PDF, JPG, PNG · Max 10 MB</p>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-primary/80 bg-primary/5 border border-primary/20 rounded-full px-3 py-1">
        <Sparkles className="h-3 w-3" />
        AI reads supplier, items, prices and expiry dates
      </div>
    </div>
  );

  if (stage === 'uploading') return (
    <div className="border border-border rounded-xl p-8 bg-card flex flex-col items-center justify-center text-center space-y-3 min-h-[180px]">
      <Loader2 className="h-8 w-8 text-primary animate-spin" />
      <p className="font-semibold text-foreground">Reading invoice…</p>
      <p className="text-sm text-muted-foreground">This usually takes 3–8 seconds</p>
    </div>
  );

  // reviewing
  const conf     = parsedData?.confidence || {};
  const itemConf = conf.items || 'medium';
  const total    = reviewItems.reduce((s, r) => s + (+r.qty || 0) * (+r.cost_price || 0), 0);

  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden">
      {/* Banner */}
      <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border-b border-emerald-100">
        <div className="flex items-center gap-2 flex-wrap">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
          <span className="text-sm font-semibold text-emerald-800">
            {reviewItems.length} item{reviewItems.length !== 1 ? 's' : ''} extracted — review then confirm
          </span>
          {itemConf !== 'high' && (
            <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${confColor(itemConf)}`}>
              {itemConf} confidence
            </span>
          )}
        </div>
        <button onClick={reset} className="text-muted-foreground hover:text-foreground transition-colors ml-2 shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Parsed header summary */}
      {(parsedData?.supplier_name || parsedData?.date || parsedData?.bill_number) && (
        <div className="px-4 py-2 bg-muted/30 border-b border-border flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground items-center">
          {parsedData.supplier_name && (
            <span className="flex items-center gap-1">
              <span className="font-medium text-foreground">Supplier:</span> {parsedData.supplier_name}
              <span className={`px-1 py-0.5 rounded text-[10px] font-bold uppercase ${confColor(conf.supplier_name || 'medium')}`}>
                {conf.supplier_name}
              </span>
            </span>
          )}
          {parsedData.date       && <span><span className="font-medium text-foreground">Date:</span> {parsedData.date}</span>}
          {parsedData.bill_number && (
            <span className="flex items-center gap-1">
              <span className="font-medium text-foreground">Bill #:</span> {parsedData.bill_number}
              <span className={`px-1 py-0.5 rounded text-[10px] font-bold uppercase ${confColor(conf.bill_number || 'medium')}`}>
                {conf.bill_number}
              </span>
            </span>
          )}
          <span className="ml-auto italic">Fields pre-filled in header above</span>
        </div>
      )}

      {/* Items table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-xs text-muted-foreground border-b border-border">
              <th className="text-left px-3 py-2.5 font-medium">Product Name</th>
              <th className="text-left px-3 py-2.5 font-medium w-20">Pack Size</th>
              <th className="text-left px-3 py-2.5 font-medium w-16">Qty</th>
              <th className="text-left px-3 py-2.5 font-medium w-24">Cost Price</th>
              <th className="text-left px-3 py-2.5 font-medium w-24">MRP</th>
              <th className="text-left px-3 py-2.5 font-medium w-20">GST %</th>
              <th className="text-left px-3 py-2.5 font-medium w-28">Expiry</th>
              <th className="text-right px-3 py-2.5 font-medium w-20">Total</th>
              <th className="w-8 px-2 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {reviewItems.map(row => (
              <tr key={row._id} className="hover:bg-muted/10">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <input
                      value={row.product_name}
                      onChange={e => updateRow(row._id, { product_name: e.target.value, catalog_id: null, is_new: true })}
                      className={`flex-1 text-sm border rounded px-1.5 py-0.5 outline-none ${confBorder(itemConf)}`}
                    />
                    {row.catalog_id
                      ? <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">✓ Linked</span>
                      : <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">New</span>}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <input
                    value={row.pack_size}
                    onChange={e => updateRow(row._id, { pack_size: e.target.value })}
                    placeholder="—"
                    className="w-full text-sm border border-border rounded px-1.5 py-0.5 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" min="0.01" step="any"
                    value={row.qty}
                    onChange={e => updateRow(row._id, { qty: e.target.value })}
                    className={`w-16 text-sm border rounded px-1.5 py-0.5 outline-none ${confBorder(itemConf)}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" min="0" step="any"
                    value={row.cost_price}
                    onChange={e => updateRow(row._id, { cost_price: e.target.value })}
                    className={`w-20 text-sm border rounded px-1.5 py-0.5 outline-none ${confBorder(itemConf)}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" min="0" step="any"
                    value={row.mrp ?? ''}
                    onChange={e => updateRow(row._id, { mrp: e.target.value === '' ? null : e.target.value })}
                    placeholder="MRP"
                    className="w-20 text-sm border border-border rounded px-1.5 py-0.5 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-3 py-2">
                  <GstSelect value={row.gst_rate} onChange={v => updateRow(row._id, { gst_rate: v })}
                    className="w-full py-0.5 text-xs" />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="date"
                    value={row.expiry_date}
                    onChange={e => updateRow(row._id, { expiry_date: e.target.value })}
                    className="w-full text-xs border border-border rounded px-1.5 py-0.5 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-3 py-2 text-right font-medium text-sm">
                  {fmt((+row.qty || 0) * (+row.cost_price || 0))}
                </td>
                <td className="px-2 py-2">
                  <button onClick={() => removeRow(row._id)}
                    className="p-1 text-muted-foreground hover:text-destructive rounded transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-border bg-muted/20 space-y-2">
        {parsedData?.total_amount && Math.abs(total - parsedData.total_amount) / parsedData.total_amount > 0.05 && (
          <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            Invoice total is {fmt(parsedData.total_amount)} but line items sum to {fmt(total)} — verify quantities and prices.
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
            Verify quantities and prices. MRP column updates the MRP in stock master.
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold">Total: {fmt(total)}</span>
            <Button size="sm" onClick={handleConfirm} disabled={stage === 'saving'} className="flex items-center gap-1.5">
              {stage === 'saving'
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <CheckCircle2 className="h-3.5 w-3.5" />}
              {stage === 'saving' ? 'Saving…' : onSaveDirectly ? `Add ${reviewItems.length} Items to Stock` : `Add ${reviewItems.length} Items to Purchase`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const RecordPurchase = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [method,              setMethod]              = useState('barcode');
  const [supplierName,        setSupplierName]        = useState('');
  const [supplierId,          setSupplierId]          = useState(null);
  const [supplierOpen,        setSupplierOpen]        = useState(false);
  const [supplierHighlightIdx, setSupplierHighlightIdx] = useState(-1);
  const [qaSupplierOpen,      setQaSupplierOpen]      = useState(false);
  const [qaSupplierForm,      setQaSupplierForm]      = useState({ name: '', phone: '', email: '' });
  const [qaSupplierLoading,   setQaSupplierLoading]   = useState(false);
  const [date,                setDate]                = useState(today());
  const [invoiceNo,           setInvoiceNo]           = useState('');
  const [items,               setItems]               = useState([]);
  const [saving,              setSaving]              = useState(false);

  const { data: allSuppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => suppliersAPI.getAll().then(r => r.data ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const { data: incomingCount = 0 } = useQuery({
    queryKey: ['incoming-invoice-count'],
    queryFn: () => incomingInvoicesAPI.getCount().then(r => r.data?.count ?? 0),
    staleTime: 60_000,
  });

  const { data: allCatalog = [] } = useQuery({
    queryKey: ['record-purchase-catalog'],
    queryFn: () => posAPI.getProducts().then(r => r.data?.items ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const filteredSuppliers = useMemo(() => {
    if (!supplierName.trim()) return allSuppliers.slice(0, 8);
    const q = supplierName.toLowerCase();
    return allSuppliers.filter(s => s.name.toLowerCase().includes(q)).slice(0, 8);
  }, [allSuppliers, supplierName]);

  const handleSupplierSelect = (supplier) => {
    setSupplierName(supplier.name);
    setSupplierId(supplier.supplier_id || null);
    setSupplierOpen(false);
  };

  const handleSupplierClear = () => {
    setSupplierName('');
    setSupplierId(null);
    setSupplierOpen(false);
    setSupplierHighlightIdx(-1);
  };

  const openQaSupplier = (prefillName = '') => {
    setQaSupplierForm({ name: prefillName.trim(), phone: '', email: '' });
    setQaSupplierOpen(true);
    setSupplierOpen(false);
    setSupplierHighlightIdx(-1);
  };

  const closeQaSupplier = () => {
    setQaSupplierOpen(false);
    setQaSupplierForm({ name: '', phone: '', email: '' });
  };

  const handleQuickAddSupplier = async () => {
    const name = qaSupplierForm.name.trim();
    if (!name) { toast.error('Supplier name is required'); return; }
    setQaSupplierLoading(true);
    try {
      const res = await suppliersAPI.create({ name, phone: qaSupplierForm.phone, email: qaSupplierForm.email });
      const { supplier_id, supplier_code } = res.data;
      setSupplierName(name);
      setSupplierId(supplier_id);
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      closeQaSupplier();
      toast.success(`Supplier added${supplier_code != null ? ` (Code #${supplier_code})` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add supplier');
    } finally {
      setQaSupplierLoading(false);
    }
  };

  const handleSupplierKeyDown = (e) => {
    const total = filteredSuppliers.length + 1; // +1 for Quick Add row
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!supplierOpen) { setSupplierOpen(true); setSupplierHighlightIdx(0); }
      else setSupplierHighlightIdx(i => Math.min(i + 1, total - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSupplierHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (supplierHighlightIdx >= 0 && supplierHighlightIdx < filteredSuppliers.length) {
        handleSupplierSelect(filteredSuppliers[supplierHighlightIdx]);
      } else {
        openQaSupplier(supplierName);
      }
    } else if (e.key === 'Escape') {
      setSupplierOpen(false);
      setSupplierHighlightIdx(-1);
    }
  };

  const addItem  = (item) => setItems(prev => [...prev, item]);
  const addItems = (list) => setItems(prev => [...prev, ...list]);
  const updateItem = (id, patch) => setItems(prev => prev.map(r => r._id === id ? { ...r, ...patch } : r));
  const removeItem = (id) => setItems(prev => prev.filter(r => r._id !== id));

  // Desktop: batch submit
  const handleSubmit = async () => {
    if (items.length === 0) { toast.error('Add at least one item'); return; }
    setSaving(true);
    try {
      await purchasesAPI.create({
        supplier_name: supplierName.trim(),
        supplier_id:   supplierId || undefined,
        purchase_date: date,
        bill_number:   invoiceNo.trim() || undefined,
        items: items.map(r => ({
          catalog_id:    r.catalog_id || undefined,
          product_name:  r.product_name,
          barcode:       r.barcode || undefined,
          qty:           +r.qty,
          cost_price:    +r.cost_price,
          mrp: r.mrp ? +r.mrp : undefined,
          gst_rate:      r.gst_rate ?? undefined,
          expiry_date:   r.expiry_date || undefined,
        })),
        gst_inclusive: true,
        payment_mode:  'cash',
      });
      // Update catalog mrp for existing products where it changed
      await Promise.allSettled(
        items
          .filter(r => r.catalog_id && r.mrp && r.original_mrp !== undefined
            && +r.mrp !== +r.original_mrp)
          .map(r => catalogAPI.update(r.catalog_id, { mrp: +r.mrp }))
      );
      toast.success('Purchase recorded successfully');
      markProductsDirty();
      qc.invalidateQueries({ queryKey: ['stock-products'] });
      qc.invalidateQueries({ queryKey: ['stock-reorder'] });
      setItems([]); setSupplierName(''); setSupplierId(null); setDate(today()); setInvoiceNo('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record purchase');
    } finally {
      setSaving(false);
    }
  };

  // Mobile: direct save a single scanned item
  const saveItemNow = useCallback(async (item) => {
    try {
      await purchasesAPI.create({
        supplier_name: supplierName.trim(),
        supplier_id:   supplierId || undefined,
        purchase_date: today(),
        items: [{
          catalog_id:    item.catalog_id || undefined,
          product_name:  item.product_name,
          pack_size:     item.pack_size || undefined,
          barcode:       item.barcode || undefined,
          qty:           +item.qty,
          cost_price:    +item.cost_price,
          mrp: item.mrp ? +item.mrp : undefined,
          gst_rate:      item.gst_rate ?? undefined,
          expiry_date:   item.expiry_date || undefined,
        }],
        gst_inclusive: true,
        payment_mode:  'cash',
      });
      // Update catalog mrp if changed for existing product
      if (item.catalog_id && item.mrp && item.original_mrp !== undefined
          && +item.mrp !== +item.original_mrp) {
        catalogAPI.update(item.catalog_id, { mrp: +item.mrp }).catch(() => {});
      }
      toast.success(`${item.product_name} added to stock`);
      markProductsDirty();
      qc.invalidateQueries({ queryKey: ['stock-products'] });
      qc.invalidateQueries({ queryKey: ['stock-reorder'] });
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save — please try again');
      return false;
    }
  }, [supplierName, supplierId, qc]);

  // Mobile: direct save all invoice items at once
  const saveAllNow = useCallback(async (itemsList) => {
    try {
      await purchasesAPI.create({
        supplier_name: supplierName.trim(),
        supplier_id:   supplierId || undefined,
        purchase_date: today(),
        items: itemsList.map(r => ({
          catalog_id:    r.catalog_id || undefined,
          product_name:  r.product_name,
          pack_size:     r.pack_size || undefined,
          qty:           +r.qty,
          cost_price:    +r.cost_price,
          mrp: r.mrp ? +r.mrp : undefined,
          gst_rate:      r.gst_rate ?? undefined,
          expiry_date:   r.expiry_date || undefined,
        })),
        gst_inclusive: true,
        payment_mode:  'cash',
      });
      markProductsDirty();
      qc.invalidateQueries({ queryKey: ['stock-products'] });
      qc.invalidateQueries({ queryKey: ['stock-reorder'] });
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save — please try again');
      return false;
    }
  }, [supplierName, supplierId, qc]);

  // ── Mobile render ────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="flex flex-col h-full bg-background">
        {/* Compact header */}
        <div className="flex-none bg-card border-b border-border">
          {/* Title row */}
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <h1 className="text-base font-semibold text-foreground whitespace-nowrap shrink-0">Purchase</h1>
            <div className="inline-flex items-center gap-0.5 bg-muted rounded-lg p-0.5 shrink-0">
              <button className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm whitespace-nowrap">
                <PackagePlus className="w-3 h-3 shrink-0" />
                Record
              </button>
              <button
                onClick={() => navigate('/purchases?view=history')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <History className="w-3 h-3 shrink-0" />
                History
              </button>
              <button
                onClick={() => navigate('/purchases?view=incoming')}
                className="relative flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <Truck className="w-3 h-3 shrink-0" />
                Incoming
                {incomingCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 flex items-center justify-center text-[9px] font-bold rounded-full bg-amber-500 text-white px-0.5 leading-none">
                    {incomingCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Supplier field */}
          <div className="px-3 pb-2 relative">
            <div className="flex items-center border border-border rounded-xl bg-background focus-within:border-primary">
              <input
                value={supplierName}
                onChange={e => { setSupplierName(e.target.value); setSupplierId(null); setSupplierOpen(true); setSupplierHighlightIdx(-1); }}
                onFocus={() => setSupplierOpen(true)}
                onBlur={() => setTimeout(() => { setSupplierOpen(false); setSupplierHighlightIdx(-1); }, 150)}
                onKeyDown={handleSupplierKeyDown}
                placeholder="Supplier (optional)"
                className="flex-1 text-sm px-3 py-2 outline-none rounded-xl bg-transparent"
              />
              {supplierName && (
                <button onClick={handleSupplierClear} className="pr-2.5 text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {supplierOpen && (
              <ul className="absolute z-30 top-full mt-1 left-3 right-3 bg-card border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {filteredSuppliers.map((s, idx) => (
                  <li key={s.supplier_id || s.id}
                    onMouseDown={() => handleSupplierSelect(s)}
                    onMouseEnter={() => setSupplierHighlightIdx(idx)}
                    className={`px-3 py-2.5 text-sm cursor-pointer first:rounded-t-xl flex items-center gap-2 ${
                      idx === supplierHighlightIdx ? 'bg-primary/10' : 'hover:bg-muted/50'
                    }`}>
                    <span className="flex-1 truncate">{s.name}</span>
                    {s.is_portal_connected && (
                      <span className="shrink-0 inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-semibold leading-none">
                        Connected
                      </span>
                    )}
                    {s.supplier_code != null && <span className="font-mono text-xs text-primary/70">#{s.supplier_code}</span>}
                  </li>
                ))}
                <li
                  onMouseDown={() => openQaSupplier(supplierName)}
                  onMouseEnter={() => setSupplierHighlightIdx(filteredSuppliers.length)}
                  className={`px-3 py-2.5 text-sm cursor-pointer rounded-b-xl flex items-center gap-2 border-t border-border ${
                    filteredSuppliers.length > 0 ? '' : 'rounded-t-xl'
                  } ${supplierHighlightIdx === filteredSuppliers.length ? 'bg-amber-50' : 'hover:bg-amber-50/50'}`}>
                  <Plus className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  <span className="text-amber-700 font-medium">
                    {supplierName.trim() ? `Quick Add "${supplierName.trim()}"` : 'Quick Add Supplier'}
                  </span>
                </li>
              </ul>
            )}
          </div>

          {/* Method toggle: Barcode Scan | Upload Invoice */}
          <div className="px-3 pb-3">
            <div className="inline-flex w-full bg-muted rounded-lg p-0.5 gap-0.5">
              {[
                { id: 'barcode',  label: 'Barcode Scan',   Icon: ScanLine },
                { id: 'invoice',  label: 'Upload Invoice',  Icon: Upload   },
              ].map(m => (
                <button key={m.id} onClick={() => setMethod(m.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-all ${
                    method === m.id
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}>
                  <m.Icon className="h-3.5 w-3.5" />
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Panel content */}
        <div className="flex-1 overflow-auto p-3">
          {method === 'barcode' && (
            <BarcodeScanPanel onAdd={addItem} onSaveNow={saveItemNow} products={allCatalog} />
          )}
          {method === 'invoice' && (
            <UploadInvoicePanel
              onStageAll={addItems}
              onSaveDirectly={saveAllNow}
              catalog={allCatalog}
              onParsedMeta={({ supplier_name }) => {
                if (supplier_name) { setSupplierName(supplier_name); setSupplierId(null); }
              }}
            />
          )}
        </div>

        {/* Quick Add Supplier modal — mobile */}
        {qaSupplierOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 pt-5 pb-4 flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-100 mb-0.5">New supplier</p>
                  <h2 className="text-lg font-bold text-white leading-tight flex items-center gap-2">
                    <Truck className="h-4 w-4" /> Quick Add Supplier
                  </h2>
                </div>
                <button onClick={closeQaSupplier} className="text-white/70 hover:text-white transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Supplier Name <span className="text-red-500">*</span></label>
                  <input type="text" value={qaSupplierForm.name} autoFocus
                    onChange={e => setQaSupplierForm(f => ({ ...f, name: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleQuickAddSupplier(); if (e.key === 'Escape') closeQaSupplier(); }}
                    placeholder="e.g. Raj Traders"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone <span className="text-muted-foreground text-xs font-normal">(optional)</span></label>
                  <input type="tel" value={qaSupplierForm.phone}
                    onChange={e => setQaSupplierForm(f => ({ ...f, phone: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleQuickAddSupplier(); if (e.key === 'Escape') closeQaSupplier(); }}
                    placeholder="e.g. 9876543210"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email <span className="text-muted-foreground text-xs font-normal">(optional)</span></label>
                  <input type="email" value={qaSupplierForm.email}
                    onChange={e => setQaSupplierForm(f => ({ ...f, email: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleQuickAddSupplier(); if (e.key === 'Escape') closeQaSupplier(); }}
                    placeholder="e.g. supplier@example.com"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
                </div>
                <div className="flex gap-3 pt-1">
                  <button onClick={closeQaSupplier} disabled={qaSupplierLoading}
                    className="flex-1 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60 disabled:opacity-50">
                    Cancel
                  </button>
                  <button onClick={handleQuickAddSupplier}
                    disabled={!qaSupplierForm.name.trim() || qaSupplierLoading}
                    className="flex-1 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                    {qaSupplierLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
                    {qaSupplierLoading ? 'Adding…' : 'Add Supplier'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Desktop render ───────────────────────────────────────────────────────────
  const METHODS = [
    { id: 'barcode', label: 'Barcode Scan',   Icon: ScanLine },
    { id: 'manual',  label: 'Manual Entry',   Icon: Plus     },
    { id: 'invoice', label: 'Upload Invoice', Icon: Upload   },
  ];

  const showSupplierDrop = supplierOpen;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Compact page header */}
      <div className="flex-none flex items-center justify-between px-6 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          <PackagePlus className="h-5 w-5 text-primary shrink-0" />
          <span className="text-xl font-bold text-foreground">Record Purchase</span>
        </div>
        <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm">
            <PackagePlus className="w-3.5 h-3.5" />
            Record Purchase
          </button>
          <button
            onClick={() => navigate('/purchases?view=history')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground"
          >
            <History className="w-3.5 h-3.5" />
            Purchase History
          </button>
          <button
            onClick={() => navigate('/purchases?view=incoming')}
            className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground"
          >
            <Truck className="w-3.5 h-3.5" />
            Incoming Invoices
            {incomingCount > 0 && (
              <span className="ml-1 min-w-[18px] h-4 inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-amber-500 text-white px-1 leading-none">
                {incomingCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 lg:p-8">
        <div className="space-y-3">
          {/* Supplier + Invoice # + method toggle on one line */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <div className="flex items-center border border-border rounded-xl bg-card focus-within:border-primary">
                <input
                  value={supplierName}
                  onChange={e => { setSupplierName(e.target.value); setSupplierId(null); setSupplierOpen(true); setSupplierHighlightIdx(-1); }}
                  onFocus={() => setSupplierOpen(true)}
                  onBlur={() => setTimeout(() => { setSupplierOpen(false); setSupplierHighlightIdx(-1); }, 150)}
                  onKeyDown={handleSupplierKeyDown}
                  placeholder="Supplier (optional)"
                  className="flex-1 text-sm px-3 py-2.5 outline-none rounded-xl bg-transparent"
                />
                {supplierName && (
                  <button onClick={handleSupplierClear} className="pr-2.5 text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {showSupplierDrop && (
                <ul className="absolute z-20 top-full mt-1 w-full bg-card border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {filteredSuppliers.map((s, idx) => (
                    <li
                      key={s.supplier_id || s.id}
                      onMouseDown={() => handleSupplierSelect(s)}
                      onMouseEnter={() => setSupplierHighlightIdx(idx)}
                      className={`px-3 py-2.5 text-sm cursor-pointer first:rounded-t-xl flex items-center gap-2 ${
                        idx === supplierHighlightIdx ? 'bg-primary/10' : 'hover:bg-muted/50'
                      }`}
                    >
                      <span className="flex-1 truncate">{s.name}</span>
                      {s.is_portal_connected && (
                        <span className="shrink-0 inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-semibold leading-none">
                          Connected
                        </span>
                      )}
                      {s.supplier_code != null && <span className="font-mono text-xs text-primary/70">#{s.supplier_code}</span>}
                      {s.phone && <span className="text-xs text-muted-foreground">{s.phone}</span>}
                    </li>
                  ))}
                  <li
                    onMouseDown={() => openQaSupplier(supplierName)}
                    onMouseEnter={() => setSupplierHighlightIdx(filteredSuppliers.length)}
                    className={`px-3 py-2.5 text-sm cursor-pointer rounded-b-xl flex items-center gap-2 border-t border-border ${
                      filteredSuppliers.length > 0 ? '' : 'rounded-t-xl'
                    } ${supplierHighlightIdx === filteredSuppliers.length ? 'bg-amber-50' : 'hover:bg-amber-50/50'}`}
                  >
                    <Plus className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                    <span className="text-amber-700 font-medium">
                      {supplierName.trim() ? `Quick Add "${supplierName.trim()}"` : 'Quick Add Supplier'}
                    </span>
                  </li>
                </ul>
              )}
            </div>
            <input
              value={invoiceNo}
              onChange={e => setInvoiceNo(e.target.value)}
              placeholder="Invoice # (optional)"
              className="flex-1 text-sm border border-border rounded-xl px-3 py-2.5 outline-none focus:border-primary bg-background"
            />
            <div className="inline-flex bg-muted rounded-lg p-0.5 gap-0.5 shrink-0">
              {METHODS.map(m => (
                <button
                  key={m.id}
                  onClick={() => setMethod(m.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                    method === m.id
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <m.Icon className="h-3.5 w-3.5" />
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Active method panel */}
          {method === 'manual'  && <ManualEntryPanel  onAdd={addItem} catalog={allCatalog} />}
          {method === 'barcode' && <BarcodeScanPanel  onAdd={addItem} products={allCatalog} />}
          {method === 'invoice' && (
            <UploadInvoicePanel
              onStageAll={addItems}
              catalog={allCatalog}
              onParsedMeta={({ supplier_name, date: d, bill_number }) => {
                if (supplier_name) { setSupplierName(supplier_name); setSupplierId(null); }
                if (d) setDate(d);
                if (bill_number) setInvoiceNo(bill_number);
              }}
            />
          )}

          {/* Staged items */}
          {items.length > 0
            ? <StagedTable items={items} onUpdate={updateItem} onRemove={removeItem} />
            : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground border border-dashed border-border rounded-xl px-4 py-5">
                <AlertCircle className="h-4 w-4 shrink-0" />
                No items staged yet — add items using one of the methods above.
              </div>
            )
          }

          {/* Submit */}
          {items.length > 0 && (
            <div className="flex justify-end pt-2">
              <Button onClick={handleSubmit} disabled={saving} className="flex items-center gap-2 px-6">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackagePlus className="h-4 w-4" />}
                {saving ? 'Recording…' : 'Record Purchase'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Add Supplier popup modal — shared mobile + desktop */}
      {qaSupplierOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 pt-5 pb-4 flex items-start justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-100 mb-0.5">New supplier</p>
                <h2 className="text-lg font-bold text-white leading-tight flex items-center gap-2">
                  <Truck className="h-4 w-4" /> Quick Add Supplier
                </h2>
              </div>
              <button onClick={closeQaSupplier} className="text-white/70 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Supplier Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={qaSupplierForm.name}
                  autoFocus
                  onChange={e => setQaSupplierForm(f => ({ ...f, name: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleQuickAddSupplier(); if (e.key === 'Escape') closeQaSupplier(); }}
                  placeholder="e.g. Raj Traders"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Phone <span className="text-muted-foreground text-xs font-normal">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={qaSupplierForm.phone}
                  onChange={e => setQaSupplierForm(f => ({ ...f, phone: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleQuickAddSupplier(); if (e.key === 'Escape') closeQaSupplier(); }}
                  placeholder="e.g. 9876543210"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Email <span className="text-muted-foreground text-xs font-normal">(optional)</span>
                </label>
                <input
                  type="email"
                  value={qaSupplierForm.email}
                  onChange={e => setQaSupplierForm(f => ({ ...f, email: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleQuickAddSupplier(); if (e.key === 'Escape') closeQaSupplier(); }}
                  placeholder="e.g. supplier@example.com"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={closeQaSupplier}
                  disabled={qaSupplierLoading}
                  className="flex-1 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleQuickAddSupplier}
                  disabled={!qaSupplierForm.name.trim() || qaSupplierLoading}
                  className="flex-1 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {qaSupplierLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
                  {qaSupplierLoading ? 'Adding…' : 'Add Supplier'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordPurchase;
